import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ClipboardText, Check, X } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SectionHeader, EmptyState, fmtDate } from "./ui";

export default function Approvals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/cc/change-requests", { params: { status: "pending" } }); setRows(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (req, action) => {
    setBusy(req.id);
    try {
      await api.post(`/cc/change-requests/${req.id}/${action}`);
      setRows((r) => r.filter((x) => x.id !== req.id));
      toast.success(action === "approve" ? "Changes applied" : "Request rejected");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="approvals-page">
      <SectionHeader title="Approvals" subtitle="Business info changes clients requested from their portal. Approve to apply them." />
      {loading ? <div className="text-muted-foreground">Loading…</div> :
        rows.length === 0 ? <EmptyState icon={ClipboardText} title="Nothing to approve" hint="Client-requested business info changes will appear here." /> : (
          <div className="space-y-4" data-testid="approvals-list">
            {rows.map((req) => (
              <div key={req.id} className="rounded-xl border border-white/10 bg-zinc-900/50 p-5" data-testid={`approval-${req.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium">{req.company_name || "Business"}</div>
                    <div className="text-xs text-muted-foreground">Requested by {req.client_name || "client"} · {fmtDate(req.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" disabled={busy === req.id} onClick={() => act(req, "approve")} data-testid={`approve-${req.id}`} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1"><Check size={16} /> Approve</Button>
                    <Button size="sm" variant="outline" disabled={busy === req.id} onClick={() => act(req, "reject")} data-testid={`reject-${req.id}`} className="border-white/15 gap-1"><X size={16} /> Reject</Button>
                  </div>
                </div>
                <div className="mt-4 rounded-lg border border-white/10 divide-y divide-white/5 text-sm">
                  {Object.entries(req.changes || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between px-3 py-2">
                      <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="font-medium truncate ml-4">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
